import React, { useCallback, useEffect, useRef, useState } from "react";
import { Rnd } from "react-rnd";
import styled from "styled-components";
import { colors, font, layout, radius, shadow } from "./theme";
import { useWindowManager } from "./WindowManager";
import AppIcon from "./AppIcon";
import type { AppConfig, WindowState } from "./types";

const Frame = styled.div<{ $focused: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 2px solid ${colors.ink};
  border-radius: ${radius.lg};
  background: ${colors.window};
  box-shadow: ${({ $focused }) =>
    $focused ? shadow.windowFocused : shadow.window};
  transform-origin: center center;
  will-change: transform, opacity;
`;

const TitleBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  flex: 0 0 40px;
  padding: 0 12px;
  background: ${colors.windowHeader};
  border-bottom: 2px solid ${colors.ink};
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

const Lights = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  &:hover svg {
    opacity: 1;
  }
`;

const Light = styled.button<{ $color: string }>`
  width: 14px;
  height: 14px;
  border-radius: ${radius.pill};
  border: 1.5px solid rgba(0, 0, 0, 0.45);
  background: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  svg {
    opacity: 0;
    width: 9px;
    height: 9px;
    color: rgba(0, 0, 0, 0.6);
    transition: opacity 0.12s ease;
  }
`;

const TitleText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${font.body};
  font-weight: 700;
  font-size: 13.5px;
  color: ${colors.ink};
  margin: 0 auto;
  padding-right: 52px;
`;

const Content = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
`;

const Glyph = {
  Close: () => (
    <svg viewBox="0 0 12 12" fill="none">
      <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  Min: () => (
    <svg viewBox="0 0 12 12" fill="none">
      <path d="M3 6h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  Max: () => (
    <svg viewBox="0 0 12 12" fill="none">
      <path d="M3.5 3.5h5v5h-5z" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  ),
};

const Window = ({
  win,
  app,
  area,
}: {
  win: WindowState;
  app: AppConfig;
  area: { width: number; height: number };
}) => {
  const {
    focusedId,
    focusWindow,
    closeWindow,
    minimizeWindow,
    toggleMaximize,
    moveWindow,
    resizeWindow,
  } = useWindowManager();

  const AppComponent = app.component;
  const focused = focusedId === win.id;

  const maximizedSize = {
    width: area.width,
    height: Math.max(240, area.height),
  };
  const size = win.maximized
    ? maximizedSize
    : { width: win.width, height: win.height };
  const position = win.maximized ? { x: 0, y: 0 } : { x: win.x, y: win.y };

  const prefersReduced =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Animation phase for the minimize/restore "scale into the dock" effect.
  type Phase = "open" | "idle" | "min" | "restore" | "hidden";
  const [phase, setPhase] = useState<Phase>(win.minimized ? "hidden" : "open");
  const [genie, setGenie] = useState({ gx: 0, gy: 0, gs: 0.1 });
  const prevMinimized = useRef(win.minimized);

  // Compute the transform that lands this window on its dock slot. Values are
  // in viewport pixels relative to the window's current center.
  const buildGenie = useCallback(() => {
    if (typeof window === "undefined") return { gx: 0, gy: 0, gs: 0.1 };
    const w = win.maximized ? area.width : win.width;
    const h = win.maximized ? Math.max(240, area.height) : win.height;
    const left = win.maximized ? 0 : win.x;
    const top = layout.menuBarHeight + (win.maximized ? 0 : win.y);
    const cx = left + w / 2;
    const cy = top + h / 2;

    let targetX = window.innerWidth / 2;
    let targetY = window.innerHeight - 40;
    const tile = document.querySelector(`[data-win-tile="${win.id}"]`);
    const bar = document.querySelector("[data-dock]");
    if (tile) {
      const r = (tile as HTMLElement).getBoundingClientRect();
      targetX = r.left + r.width / 2;
      targetY = r.top + r.height / 2;
    } else if (bar) {
      const r = (bar as HTMLElement).getBoundingClientRect();
      targetX = r.right - 26;
      targetY = r.top + r.height / 2;
    }
    const gs = Math.max(0.05, Math.min(0.14, 46 / Math.max(w, h)));
    return { gx: targetX - cx, gy: targetY - cy, gs };
  }, [
    area.width,
    area.height,
    win.maximized,
    win.width,
    win.height,
    win.x,
    win.y,
    win.id,
  ]);

  useEffect(() => {
    if (win.minimized === prevMinimized.current) return;
    prevMinimized.current = win.minimized;
    if (win.minimized) {
      if (prefersReduced) return setPhase("hidden");
      setGenie(buildGenie());
      setPhase("min");
    } else {
      if (prefersReduced) return setPhase("idle");
      setGenie(buildGenie());
      setPhase("restore");
    }
  }, [win.minimized, prefersReduced, buildGenie]);

  const handleAnimationEnd = (e: React.AnimationEvent<HTMLDivElement>) => {
    // Ignore animations bubbling up from app content inside the window.
    if (e.target !== e.currentTarget) return;
    setPhase((p) =>
      p === "min" ? "hidden" : p === "restore" || p === "open" ? "idle" : p
    );
  };

  const animationCss =
    phase === "open"
      ? "windowOpen 0.18s ease-out forwards"
      : phase === "min"
      ? "genieOut 0.34s cubic-bezier(0.4, 0, 0.2, 1) forwards"
      : phase === "restore"
      ? "genieIn 0.36s cubic-bezier(0.2, 0.85, 0.3, 1) forwards"
      : "none";

  const frameStyle = {
    animation: animationCss,
    "--gx": `${genie.gx}px`,
    "--gy": `${genie.gy}px`,
    "--gs": `${genie.gs}`,
  } as React.CSSProperties;

  return (
    <Rnd
      size={size}
      position={position}
      bounds="parent"
      minWidth={320}
      minHeight={240}
      dragHandleClassName="win-titlebar"
      disableDragging={win.maximized}
      enableResizing={!win.maximized}
      onDragStart={() => focusWindow(win.id)}
      onResizeStart={() => focusWindow(win.id)}
      onDragStop={(_e, d) => moveWindow(win.id, d.x, d.y)}
      onResizeStop={(_e, _dir, ref, _delta, pos) =>
        resizeWindow(
          win.id,
          { x: pos.x, y: pos.y },
          { width: ref.offsetWidth, height: ref.offsetHeight }
        )
      }
      style={{
        zIndex: win.z,
        display: phase === "hidden" ? "none" : "flex",
        pointerEvents: phase === "min" ? "none" : "auto",
      }}
    >
      <Frame
        $focused={focused}
        onMouseDown={() => focusWindow(win.id)}
        onAnimationEnd={handleAnimationEnd}
        style={frameStyle}
      >
        <TitleBar className="win-titlebar" onDoubleClick={() => toggleMaximize(win.id)}>
          <Lights>
            <Light
              $color={colors.close}
              title="Close"
              onClick={(e) => {
                e.stopPropagation();
                closeWindow(win.id);
              }}
            >
              <Glyph.Close />
            </Light>
            <Light
              $color={colors.minimize}
              title="Minimize"
              onClick={(e) => {
                e.stopPropagation();
                minimizeWindow(win.id);
              }}
            >
              <Glyph.Min />
            </Light>
            <Light
              $color={colors.maximize}
              title="Maximize"
              onClick={(e) => {
                e.stopPropagation();
                toggleMaximize(win.id);
              }}
            >
              <Glyph.Max />
            </Light>
          </Lights>
          <TitleText>
            <AppIcon
              icon={app.icon}
              size={15}
              color={app.accent === colors.ink ? colors.inkSoft : app.accent}
            />
            {app.title}
          </TitleText>
        </TitleBar>
        <Content>
          <AppComponent />
        </Content>
      </Frame>
    </Rnd>
  );
};

export default Window;
